import { useCallback, useContext, useMemo } from 'react';

import BaseViewsContext from 'contexts/BaseViewsContext';
import CardContext from 'contexts/CardContext';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import GqlClientContext from 'contexts/GqlClientContext';
import ToastContext from 'contexts/ToastContext';
import exportGrid from 'components/Grid/api/exportGrid';
import ExportGridContext from 'components/Grid/context/ExportGridContext';
import { TOTALS_QUERY } from 'components/Grid/gridConstants';
import search from 'hooks/grids/search';
import useDateScope from 'hooks/useDateScope';
import useFilterInput from 'hooks/useFilterInput';
import useGetFieldLabel from 'hooks/useGetFieldLabel';
import isRemindersGadget from 'types/visTypeCheckers/isRemindersGadget';

// Reminders gadgets are backed by the `upcomingEvents` dataset (see
// useDashboardGadgetDataTypes), so the export re-runs that dataset server-side.
const DATA_TYPE = 'upcomingEvents';

// The reminders gadget's data contract is the `getReminders` GraphQL selection
// (name, date, driver, driverName, employeeName, truck, overdue, description).
// Mapped to `upcomingEvents` dataset fields, dropping the ones that aren't real
// dataset columns: `name` -> `eventName`, `overdue` is derived from the date,
// `key` is client-generated, and `employeeName` has no dataset field. Order here
// is the column order in the exported file.
const REMINDER_EXPORT_FIELDS = [
  'date',
  'eventName',
  'driver',
  'driverName',
  'truck',
  'description',
];

const isPopulated = (value: unknown) =>
  value !== null && value !== undefined && value !== '';

const useExportReminders = () => {
  const { chartDefinition } = useContext(CardContext);
  const { baseViews, ensureBaseViewIsPersisted } = useContext(BaseViewsContext);
  const { api } = useContext(CloudFunctionClientContext);
  const { client } = useContext(GqlClientContext);
  const { showToast } = useContext(ToastContext);
  const {
    isExportPending,
    onExportRequestStarted,
    onExportRequestResponse,
    onExportRequestFailed,
  } = useContext(ExportGridContext);
  const { getFieldLabel } = useGetFieldLabel();
  const dateScope = useDateScope({});
  const { filterInput } = useFilterInput({});

  const events = useMemo(
    () =>
      chartDefinition && isRemindersGadget(chartDefinition)
        ? chartDefinition.events
        : [],
    [chartDefinition],
  );

  const exportReminders = useCallback(async () => {
    const baseView = baseViews[DATA_TYPE];
    if (!baseView) {
      showToast('Error exporting grid');
      return;
    }

    // Mirror `useReminders`: send the gadget's filterInput and constrain to the
    // configured reminder events (the resolver applies these as reminderEventNames;
    // for the dataset export we express them as an `eventName` keyword filter).
    const filters: FilterInput[] = [
      {
        ...filterInput,
        keywords: [
          ...(filterInput.keywords ?? []),
          { field: 'eventName', values: events, exclude: false },
        ],
      },
    ];

    const sortBy: GridSortField[] = [{ field: 'date', sort: 'asc' }];

    // Fields the reminders contract could surface that exist in this account's
    // dataset. A single reminders gadget can mix event types (driver vs vehicle),
    // so some of these are only relevant for some events.
    const candidateFields = REMINDER_EXPORT_FIELDS.filter(
      (field) => baseView.fields[field],
    );

    // Pre-flight: sample the rows that will be exported and keep only the columns
    // that actually contain data, so irrelevant fields (e.g. `truck`/`description`
    // for driver-licence reminders) don't come through as empty columns. If the
    // sample is empty or the query fails, fall back to all candidate fields so the
    // export still has headers.
    const populatedFields = await (async () => {
      try {
        const { documents } = await search({
          dataType: DATA_TYPE,
          filters,
          dateScope,
          sortBy,
          client,
          includeFields: candidateFields,
        });

        if (documents.length === 0) {
          return candidateFields;
        }

        const populated = candidateFields.filter((field) =>
          documents.some((document) => isPopulated(document[field])),
        );

        return populated.length > 0 ? populated : candidateFields;
      } catch {
        return candidateFields;
      }
    })();

    const include_fields = populatedFields.map((field) => ({
      field,
      label: getFieldLabel({ field, dataType: DATA_TYPE }),
    }));

    const name =
      `FleetOps Reminders export - ${dateScope.startDate} - ${dateScope.endDate}`.replaceAll(
        '.',
        '',
      );

    await ensureBaseViewIsPersisted(baseView);
    onExportRequestStarted(name);
    const response = await exportGrid({
      api,
      name,
      data_type: DATA_TYPE,
      filters,
      date_scope: dateScope,
      sort_by: sortBy,
      include_fields,
      totals_fields: [],
      totals_query: TOTALS_QUERY,
      baseView,
    });

    if (response.ok && response.data) {
      onExportRequestResponse(response.data.exportDocumentId);
    } else {
      onExportRequestFailed();
      showToast('Error exporting grid');
    }
  }, [
    api,
    baseViews,
    client,
    dateScope,
    ensureBaseViewIsPersisted,
    events,
    filterInput,
    getFieldLabel,
    onExportRequestFailed,
    onExportRequestResponse,
    onExportRequestStarted,
    showToast,
  ]);

  return { exportReminders, isExportPending };
};

export default useExportReminders;
